import { useState, type FormEvent } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Eye, EyeOff, AlertTriangle, Loader2, Mail, Lock, ArrowRight } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { ApiError } from '@/api/client';
import { AuthShell, HipaaBadge, AuthErrorBanner } from '@/components/auth/AuthShell';

export function LoginPage() {
  const { loginPassword, lastLogoutReason } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const notice = (location.state as { notice?: string } | null)?.notice ?? null;
  const [email, setEmail]               = useState('');
  const [password, setPassword]         = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting]     = useState(false);
  const [error, setError]               = useState<string | null>(null);
  const [emailError, setEmailError]     = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);

  function validatePassword(val: string): boolean {
    if (!val) { setPasswordError('Password is required.'); return false; }
    setPasswordError(null);
    return true;
  }

  function validateEmail(val: string): boolean {
    const v = val.trim();
    if (!v) { setEmailError('Email is required.'); return false; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) {
      setEmailError('Please enter a valid email address.'); return false;
    }
    setEmailError(null);
    return true;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!validateEmail(email)) return;
    if (!validatePassword(password)) return;
    setError(null);
    setSubmitting(true);
    try {
      const next = await loginPassword(email.trim(), password);
      if (next === 'mfa_enrol_required') navigate('/auth/mfa/enrol');
      else navigate('/auth/mfa/challenge');
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === 'RATE_LIMITED') {
          setError('Too many sign-in attempts. Please wait a moment before trying again.');
        } else if (err.code === 'ACCOUNT_LOCKED') {
          setError('Your account has been temporarily locked due to multiple failed login attempts. Please contact your administrator to unlock it.');
        } else if (err.code === 'VALIDATION_ERROR') {
          setError('Please enter a valid email address and password.');
        } else {
          setError('The email or password you entered is incorrect.');
        }
      } else {
        setError('Sign-in is temporarily unavailable. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthShell
      title="Clinical Trial Management System"
      lede={<>Streamline clinical trials. Ensure compliance.{'\n'}Deliver life-changing outcomes.</>}
    >
      {/* Idle logout banner */}
      {lastLogoutReason === 'idle' && (
        <div className="flex items-start gap-2 bg-high-soft border border-high/40 rounded-md px-3.5 py-2.5 text-[11px] text-high mb-5">
          <AlertTriangle size={13} className="mt-px shrink-0"/>
          <span>You were signed out after 15 minutes of inactivity.</span>
        </div>
      )}

      {/* Notice from invite / password-reset flows */}
      {notice && (
        <div className="flex items-start gap-2 bg-amber-50 border border-amber-300 rounded-md px-3.5 py-2.5 text-[11px] text-amber-800 mb-5">
          <AlertTriangle size={13} className="mt-px shrink-0"/>
          <span>{notice}</span>
        </div>
      )}

      <p className="eyebrow mb-2">Sign in</p>
      <h1 className="text-[22px] sm:text-[26px] font-extrabold text-text-1 mb-1.5 tracking-tight leading-tight">
        Welcome back
      </h1>
      <p className="text-[12.5px] text-text-3 mb-6 leading-relaxed">
        Sign in to access the Clinical Trial Management System.
      </p>

      <div className="relative bg-white border border-border rounded-sm p-5 sm:p-7 sm:pb-6 shadow-card">
        <HipaaBadge />

        <form onSubmit={handleSubmit} noValidate>
          <div className="mb-[18px]">
            <label htmlFor="email" className="label text-[10px] tracking-wider mb-2">Email</label>
            <div className="flex items-center h-[46px] gap-2.5 px-3.5 bg-white border border-border rounded-sm transition-colors focus-within:border-navy">
              <Mail size={15} className="text-text-4 shrink-0"/>
              <input
                id="email" data-testid="login-email" type="email" value={email}
                onChange={e => { setEmail(e.target.value); if (emailError) validateEmail(e.target.value); }}
                onBlur={e => validateEmail(e.target.value)}
                placeholder="you@organisation.com"
                autoComplete="email" required maxLength={60}
                className="flex-1 min-w-0 bg-transparent border-0 outline-none text-[16px] sm:text-[13.5px] text-text-1 placeholder:text-text-4"
              />
            </div>
            {emailError && <p className="text-[10.5px] text-critical mt-1 ml-0.5">{emailError}</p>}
          </div>

          <div className="mb-2.5">
            <label htmlFor="password" className="label text-[10px] tracking-wider mb-2">Password</label>
            <div className="flex items-center h-[46px] gap-2.5 px-3.5 bg-white border border-border rounded-sm transition-colors focus-within:border-navy">
              <Lock size={15} className="text-text-4 shrink-0"/>
              <input
                id="password" data-testid="login-password" type={showPassword ? 'text' : 'password'} value={password}
                onChange={e => { setPassword(e.target.value); if (passwordError) setPasswordError(null); }}
                onBlur={e => validatePassword(e.target.value)}
                placeholder="Enter password"
                autoComplete="current-password" required maxLength={20}
                className="flex-1 min-w-0 bg-transparent border-0 outline-none text-[16px] sm:text-[13.5px] text-text-1 placeholder:text-text-4"
              />
              <button
                type="button" onClick={() => setShowPassword(v => !v)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                className="p-1 text-text-4 hover:text-text-2"
              >
                {showPassword ? <EyeOff size={15}/> : <Eye size={15}/>}
              </button>
            </div>
          </div>

          {passwordError && <p className="text-[10.5px] text-critical mt-1 ml-0.5">{passwordError}</p>}

          <div className="text-right mb-1">
            <Link to="/auth/forgot-password" className="text-[11px] font-bold text-blue hover:text-navy">
              Forgot password?
            </Link>
          </div>

          <div className="min-h-12 flex items-center mb-3.5">
            {error && <AuthErrorBanner message={error} />}
          </div>

          <button
            type="submit"
            data-testid="login-submit"
            disabled={submitting}
            className="w-full h-12 bg-navy hover:bg-navy-600 active:bg-navy-700 text-white font-bold text-[13.5px] tracking-wide rounded-sm flex items-center justify-center gap-1.5 transition-colors shadow-card disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {submitting
              ? <><Loader2 size={14} className="animate-spin"/> Signing in</>
              : <>Sign in <ArrowRight size={14} className="opacity-75" /></>}
          </button>

          <div className="mt-4 flex items-center justify-center gap-3 text-[11px] text-text-3">
            <a
              href="/legal/privacy"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="login-legal-privacy"
              className="hover:text-navy underline underline-offset-2 decoration-text-4/40 hover:decoration-navy"
            >
              Privacy Policy
            </a>
            <span aria-hidden className="text-text-4">·</span>
            <a
              href="/legal/terms"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="login-legal-terms"
              className="hover:text-navy underline underline-offset-2 decoration-text-4/40 hover:decoration-navy"
            >
              Terms &amp; Conditions
            </a>
            <span aria-hidden className="text-text-4">·</span>
            <a
              href="/legal/cookies"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="login-legal-cookies"
              className="hover:text-navy underline underline-offset-2 decoration-text-4/40 hover:decoration-navy"
            >
              Cookies
            </a>
          </div>
        </form>
      </div>
    </AuthShell>
  );
}
